import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle, XCircle, Sparkle } from "@phosphor-icons/react";

export default function PaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", attempts: 0 });
  const polling = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) { setState({ status: "missing" }); return; }
    if (polling.current) return;
    polling.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/stripe/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState({ status: "paid", data: r.data }); return; }
        if (r.data.status === "expired") { setState({ status: "expired" }); return; }
        if (attempts >= 8) { setState({ status: "timeout" }); return; }
        setState({ status: "pending", attempts });
        setTimeout(poll, 2000);
      } catch (err) {
        setState({ status: "error", message: err?.response?.data?.detail || "Could not verify payment" });
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }} data-testid="pay-success-page">
      <div className="max-w-md w-full">
        {state.status === "checking" && (
          <div className="text-center">
            <Sparkle size={64} weight="duotone" color="var(--accent)" className="mx-auto animate-pulse" />
            <div className="overline mt-6 mb-2">// confirming</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">Verifying payment...</h1>
          </div>
        )}
        {state.status === "pending" && (
          <div className="text-center">
            <Sparkle size={64} weight="duotone" color="var(--accent)" className="mx-auto animate-pulse" />
            <div className="overline mt-6 mb-2">// processing · attempt {state.attempts}</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">Almost done...</h1>
            <p className="mt-3" style={{ color: "var(--text-2)" }}>Stripe is confirming your payment. This usually takes 5-10 seconds.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div className="border p-10 text-center" style={{ borderColor: "var(--accent)" }} data-testid="pay-success">
            <CheckCircle size={72} weight="duotone" color="var(--accent)" className="mx-auto" />
            <div className="overline mt-6 mb-2" style={{ color: "var(--accent)" }}>// payment received</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">You're in.</h1>
            <p className="mt-3" style={{ color: "var(--text-2)" }}>
              ${(state.data.amount_total / 100).toFixed(2)} {state.data.currency.toUpperCase()} charged. Your plan is active.
            </p>
            <Link to="/dashboard" className="btn-primary mt-8 inline-flex items-center justify-center w-full" data-testid="paid-dashboard-btn">Go to dashboard</Link>
          </div>
        )}
        {state.status === "expired" && (
          <div className="border p-10 text-center" style={{ borderColor: "var(--border)" }}>
            <XCircle size={64} weight="duotone" className="mx-auto" />
            <h1 className="font-display font-black text-2xl tracking-tighter mt-6">Session expired</h1>
            <Link to="/pricing" className="btn-primary mt-6 inline-block">Back to pricing</Link>
          </div>
        )}
        {(state.status === "timeout" || state.status === "error" || state.status === "missing") && (
          <div className="border p-10 text-center" style={{ borderColor: "var(--border)" }}>
            <XCircle size={64} weight="duotone" className="mx-auto" />
            <h1 className="font-display font-black text-2xl tracking-tighter mt-6">{state.status === "missing" ? "No session" : "Could not verify"}</h1>
            <p className="mt-3" style={{ color: "var(--text-2)" }}>{state.message || "If you completed payment, refresh this page or check your email for confirmation."}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => window.location.reload()} className="btn-secondary">Retry</button>
              <Link to="/pricing" className="btn-primary">Back to pricing</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
